import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button.jsx";
import {
  CART_UPDATED_EVENT,
  clearCart,
  getCartCount,
  getCartSubtotal,
  readCart,
  removeCartItem,
  updateCartQuantity
} from "@/utils/cart";

export default function CartPage() {
  const [items, setItems] = useState([]);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  const refreshCart = () => {
    setItems(readCart());
  };

  useEffect(() => {
    refreshCart();

    window.addEventListener(CART_UPDATED_EVENT, refreshCart);
    window.addEventListener("storage", refreshCart);
    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, refreshCart);
      window.removeEventListener("storage", refreshCart);
    };
  }, []);

  const subtotal = useMemo(() => getCartSubtotal(items), [items]);
  const shipping = items.length > 0 ? 12 : 0;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;

  const handleStripeCheckout = async () => {
    if (items.length === 0) return;
    setIsCheckingOut(true);
    setCheckoutError("");
    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Checkout failed");
      window.location.href = data.url;
    } catch (err) {
      console.error("Checkout error:", err);
      setCheckoutError(err.message || "Something went wrong. Please try again.");
      setIsCheckingOut(false);
    }
  };

  return (
    <div className="min-h-screen pb-14">
      <section className="page-wrap">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="chip mb-3">Checkout System</p>
            <h1 className="text-5xl md:text-7xl leading-[0.95]">Cart</h1>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-[0.14em] text-[var(--muted)]">Items</p>
            <p className="text-2xl font-semibold">{getCartCount(items)}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 panel p-5 md:p-7">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-3xl">Bag Items</h2>
                {items.length > 0 && (
                  <button className="btn-outline px-4 py-2 text-xs uppercase tracking-[0.1em]" onClick={() => clearCart()}>
                    Clear
                  </button>
                )}
              </div>

              {items.length === 0 ? (
                <div className="border border-dashed border-[var(--line)] rounded-xl p-8 text-center">
                  <p className="text-lg mb-3">Your bag is empty.</p>
                  <Link to="/products" className="btn-brand inline-flex px-6 py-3 text-sm uppercase tracking-[0.1em]">Shop Now</Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item, index) => (
                    <article key={`${item.productId}-${item.size || "no-size"}-${index}`} className="tech-card p-4 rounded-xl">
                      <div className="grid sm:grid-cols-[96px_1fr] gap-4">
                        <div className="w-24 h-24 rounded-lg overflow-hidden bg-[var(--accent-soft)]">
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        </div>

                        <div className="flex flex-col gap-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <h3 className="text-xl">{item.name}</h3>
                              <p className="text-sm text-[var(--muted)]">{item.designer || "Studio"}</p>
                              {item.size && <p className="text-xs uppercase tracking-[0.12em] text-[var(--muted)] mt-1">Size {item.size}</p>}
                            </div>
                            <p className="text-lg font-semibold">${(Number(item.price) || 0).toFixed(2)}</p>
                          </div>

                          <div className="flex items-center justify-between gap-3">
                            <label className="text-sm text-[var(--muted)] flex items-center gap-2">
                              Qty
                              <input
                                type="number"
                                min={1}
                                value={item.quantity || 1}
                                onChange={(event) => setItems(updateCartQuantity(index, event.target.value))}
                                className="w-20 field-surface rounded-full px-3 py-1.5"
                              />
                            </label>
                            <button
                              className="text-sm uppercase tracking-[0.1em] text-[var(--muted)] hover:text-[var(--ink)]"
                              onClick={() => setItems(removeCartItem(index))}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>

            <aside className="lg:col-span-5 panel p-5 md:p-7">
              <h2 className="text-3xl mb-5">Order Summary</h2>
              <div className="space-y-4">
                <div className="tech-card rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>Shipping</span><span>${shipping.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>Tax (est.)</span><span>${tax.toFixed(2)}</span></div>
                  <div className="h-px bg-[var(--line)] my-1" />
                  <div className="flex justify-between text-base font-semibold"><span>Total</span><span>${total.toFixed(2)}</span></div>
                </div>

                {checkoutError && (
                  <p className="text-sm text-red-400">{checkoutError}</p>
                )}

                <Button
                  type="button"
                  disabled={items.length === 0 || isCheckingOut}
                  onClick={handleStripeCheckout}
                  className="w-full btn-brand h-12 text-sm uppercase tracking-[0.1em] disabled:opacity-50"
                >
                  {isCheckingOut ? "Redirecting to Stripe…" : "Checkout with Stripe"}
                </Button>

                <p className="text-xs text-center text-[var(--muted)]">
                  Secure payment powered by Stripe
                </p>
              </div>
            </aside>
          </div>
      </section>
    </div>
  );
}
